import { FormEvent, useState } from 'react'
import strings from '../locales/must-change.json'
import { changeOwnPassword } from '../lib/api'
import { PasswordInput } from '../components/PasswordInput'
import { useLang } from '../lib/i18n'

// R413: the change-password words. R537: one language at a time, under the till's switch
// (was Arabic first with the English beside it).
export type MustChangeKey = keyof typeof strings

export function Bilingual({ k }: { k: MustChangeKey }) {
  const lang = useLang()
  return (
    <span lang={lang} className="block">
      {strings[k][lang]}
    </span>
  )
}

const MIN_LENGTH = 8

interface ChangePasswordScreenProps {
  isOnline: boolean
  /** Called after a successful change: the current token is dead, so the person signs in again. */
  onChanged: () => void | Promise<void>
}

export function ChangePasswordScreen({ isOnline, onChanged }: ChangePasswordScreenProps) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<MustChangeKey | null>(null)

  const canSubmit =
    !submitting && isOnline && next.length >= MIN_LENGTH && next === confirm && next !== current

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError(null)
    setSubmitting(true)
    const result = await changeOwnPassword(current, next)
    if (result.ok) {
      await onChanged()
      return
    }
    setSubmitting(false)
    setError(
      result.reason === 'wrong-current'
        ? 'accountError403'
        : result.reason === 'locked'
          ? 'accountError429'
          : result.reason === 'offline'
            ? 'accountErrorOffline'
            : 'accountErrorOther',
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-fleet-50 px-6 font-body">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <h1 className="font-condensed text-2xl font-bold text-fleet-800 text-center">
          <Bilingual k="mustChangeTitle" />
        </h1>
        <p className="text-sm text-fleet-600 text-center">
          <Bilingual k="mustChangeBody" />
        </p>
        <div>
          <label htmlFor="change-current" className="block text-sm font-semibold text-fleet-600 mb-1.5">
            <Bilingual k="accountFieldCurrent" />
          </label>
          <PasswordInput
            id="change-current"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full min-h-tap rounded-lg border border-fleet-300 px-3 py-2"
          />
        </div>
        <div>
          <label htmlFor="change-new" className="block text-sm font-semibold text-fleet-600 mb-1.5">
            <Bilingual k="accountFieldNew" />
          </label>
          <PasswordInput
            id="change-new"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full min-h-tap rounded-lg border border-fleet-300 px-3 py-2"
          />
        </div>
        <div>
          <label htmlFor="change-confirm" className="block text-sm font-semibold text-fleet-600 mb-1.5">
            <Bilingual k="accountFieldConfirm" />
          </label>
          <PasswordInput
            id="change-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full min-h-tap rounded-lg border border-fleet-300 px-3 py-2"
          />
        </div>
        {error && (
          <div role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
            <Bilingual k={error} />
          </div>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full min-h-tap rounded-lg bg-amber-500 disabled:bg-fleet-300 text-white font-semibold px-4 py-2"
        >
          <Bilingual k={submitting ? 'accountSubmitting' : 'accountSubmit'} />
        </button>
      </form>
    </div>
  )
}
